import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, generateId, getFinancialYearStart, getFinancialYearEnd, getFinancialYear } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Car, Plus, Fuel, Shield, Wrench, Receipt, DollarSign, Trash2, Calculator } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Vehicle, VehicleExpense, VehicleExpenseType } from '@/lib/types';

const EXPENSE_TYPE_META: Record<VehicleExpenseType, { label: string; icon: typeof Fuel }> = {
  fuel: { label: 'Fuel', icon: Fuel },
  rego: { label: 'Registration', icon: Receipt },
  insurance: { label: 'Insurance', icon: Shield },
  servicing: { label: 'Servicing', icon: Wrench },
  tolls: { label: 'Tolls', icon: DollarSign },
  parking: { label: 'Parking', icon: DollarSign },
  other: { label: 'Other', icon: DollarSign },
};

function getFYOptions() {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const options: { label: string; startYear: number }[] = [];
  for (let i = 0; i < 5; i++) {
    const y = startYear - i;
    options.push({ label: `${y}-${(y + 1).toString().slice(-2)}`, startYear: y });
  }
  return options;
}

export default function VehiclesPage() {
  const { data, updateData } = useAuth();
  const vehicles = data.vehicles ?? [];
  const vehicleExpenses = data.vehicleExpenses ?? [];
  const fyOptions = useMemo(() => getFYOptions(), []);
  const [selectedFY, setSelectedFY] = useState(fyOptions[0].label);
  const selectedStartYear = fyOptions.find(o => o.label === selectedFY)!.startYear;
  const fyStart = new Date(selectedStartYear, 6, 1);
  const fyEnd = new Date(selectedStartYear + 1, 5, 30);

  // Add vehicle dialog
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vName, setVName] = useState('');
  const [vRego, setVRego] = useState('');
  const [vWork, setVWork] = useState(0);

  // Add expense dialog
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [eVehicleId, setEVehicleId] = useState('');
  const [eType, setEType] = useState<VehicleExpenseType>('fuel');
  const [eAmount, setEAmount] = useState('');
  const [eDate, setEDate] = useState(new Date().toISOString().split('T')[0]);
  const [eDesc, setEDesc] = useState('');
  const [eOdo, setEOdo] = useState('');

  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  async function addVehicle() {
    if (!vName.trim()) return;
    const v: Vehicle = {
      id: generateId(),
      name: vName.trim(),
      rego: vRego.trim() || undefined,
      workUsePercent: vWork,
      createdAt: new Date().toISOString(),
    };
    await updateData(prev => ({ ...prev, vehicles: [...(prev.vehicles ?? []), v] }));
    setVName(''); setVRego(''); setVWork(0); setVehicleOpen(false);
  }

  async function deleteVehicle(id: string) {
    await updateData(prev => ({
      ...prev,
      vehicles: (prev.vehicles ?? []).filter(v => v.id !== id),
      vehicleExpenses: (prev.vehicleExpenses ?? []).filter(e => e.vehicleId !== id),
    }));
    if (selectedVehicle === id) setSelectedVehicle(null);
  }

  async function addExpense() {
    const amt = parseFloat(eAmount);
    if (!eVehicleId || isNaN(amt) || amt <= 0) return;
    const e: VehicleExpense = {
      id: generateId(),
      vehicleId: eVehicleId,
      type: eType,
      amount: amt,
      date: eDate,
      description: eDesc.trim(),
      odometer: eOdo ? parseInt(eOdo) : undefined,
      createdAt: new Date().toISOString(),
    };
    await updateData(prev => ({ ...prev, vehicleExpenses: [...(prev.vehicleExpenses ?? []), e] }));
    setEAmount(''); setEDesc(''); setEOdo(''); setExpenseOpen(false);
  }

  async function deleteExpense(id: string) {
    await updateData(prev => ({
      ...prev,
      vehicleExpenses: (prev.vehicleExpenses ?? []).filter(e => e.id !== id),
    }));
  }

  // Stats for selected vehicle or all
  const filteredExpenses = useMemo(() => {
    return vehicleExpenses.filter(e => {
      const d = new Date(e.date);
      const inFY = d >= fyStart && d <= fyEnd;
      const matchVehicle = selectedVehicle ? e.vehicleId === selectedVehicle : true;
      return inFY && matchVehicle;
    });
  }, [vehicleExpenses, fyStart, fyEnd, selectedVehicle]);

  const stats = useMemo(() => {
    const byType: Partial<Record<VehicleExpenseType, number>> = {};
    let total = 0;
    for (const e of filteredExpenses) {
      byType[e.type] = (byType[e.type] || 0) + e.amount;
      total += e.amount;
    }
    const vehicle = selectedVehicle ? vehicles.find(v => v.id === selectedVehicle) : null;
    const workPercent = vehicle ? vehicle.workUsePercent : 0;
    const deductible = total * (workPercent / 100);
    return { byType, total, workPercent, deductible };
  }, [filteredExpenses, selectedVehicle, vehicles]);

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <Car className="h-6 w-6 text-primary" />
            Vehicles
          </h1>
          <p className="text-sm text-muted-foreground">Track vehicle costs & work-related deductions</p>
        </div>
        <Select value={selectedFY} onValueChange={setSelectedFY}>
          <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fyOptions.map(o => (
              <SelectItem key={o.label} value={o.label}>FY {o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vehicle selector / add */}
      <div className="mb-4 flex gap-2">
        <Select value={selectedVehicle ?? 'all'} onValueChange={v => setSelectedVehicle(v === 'all' ? null : v)}>
          <SelectTrigger className="flex-1"><SelectValue placeholder="All vehicles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vehicles</SelectItem>
            {vehicles.map(v => (
              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Dialog open={vehicleOpen} onOpenChange={setVehicleOpen}>
          <DialogTrigger asChild>
            <Button size="icon" variant="outline"><Plus className="h-4 w-4" /></Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Vehicle</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input placeholder="e.g. 2020 Toyota Corolla" value={vName} onChange={e => setVName(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Registration (optional)</Label>
                <Input placeholder="e.g. ABC123" value={vRego} onChange={e => setVRego(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Work-related use: {vWork}%</Label>
                <Slider value={[vWork]} onValueChange={v => setVWork(v[0])} max={100} step={1} className="mt-2" />
                <p className="text-xs text-muted-foreground mt-1">
                  Percentage of vehicle use that is work-related (for tax deductions)
                </p>
              </div>
              <Button className="w-full" onClick={addVehicle} disabled={!vName.trim()}>Add Vehicle</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {vehicles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Car className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No vehicles added yet. Add a vehicle to start tracking expenses.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid gap-3 mb-4">
            <Card>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
                  <DollarSign className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Costs (FY {selectedFY})</p>
                  <p className="font-display text-xl font-bold">{formatAUD(stats.total)}</p>
                </div>
              </CardContent>
            </Card>

            {selectedVehicle && stats.workPercent > 0 && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Calculator className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Work-Related Deduction ({stats.workPercent}%)</p>
                    <p className="font-display text-xl font-bold text-primary">{formatAUD(stats.deductible)}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Cost breakdown by type */}
          {Object.keys(stats.byType).length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(Object.entries(stats.byType) as [VehicleExpenseType, number][])
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, total]) => {
                    const meta = EXPENSE_TYPE_META[type];
                    return (
                      <div key={type} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <meta.icon className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-medium">{meta.label}</p>
                        </div>
                        <Badge variant="secondary" className="font-display font-semibold">{formatAUD(total)}</Badge>
                      </div>
                    );
                  })}
              </CardContent>
            </Card>
          )}

          {/* Add expense button */}
          <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
            <DialogTrigger asChild>
              <Button className="w-full mb-4 gap-2" onClick={() => {
                if (!eVehicleId && vehicles.length > 0) setEVehicleId(selectedVehicle || vehicles[0].id);
              }}>
                <Plus className="h-4 w-4" /> Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Vehicle Expense</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Vehicle</Label>
                  <Select value={eVehicleId} onValueChange={setEVehicleId}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                    <SelectContent>
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={eType} onValueChange={v => setEType(v as VehicleExpenseType)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(EXPENSE_TYPE_META).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount ($)</Label>
                  <Input type="number" placeholder="0.00" value={eAmount} onChange={e => setEAmount(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={eDate} onChange={e => setEDate(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input placeholder="e.g. Shell Caltex" value={eDesc} onChange={e => setEDesc(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Odometer (optional)</Label>
                  <Input type="number" placeholder="km" value={eOdo} onChange={e => setEOdo(e.target.value)} className="mt-1" />
                </div>
                <Button className="w-full" onClick={addExpense} disabled={!eVehicleId || !eAmount}>Add Expense</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Recent expenses */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent Expenses</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {filteredExpenses.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No expenses recorded for this period.</p>
              ) : (
                filteredExpenses
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .slice(0, 20)
                  .map(e => {
                    const meta = EXPENSE_TYPE_META[e.type];
                    const vehicle = vehicles.find(v => v.id === e.vehicleId);
                    return (
                      <div key={e.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <meta.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {meta.label}{e.description ? ` · ${e.description}` : ''}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(e.date).toLocaleDateString('en-AU')}
                              {!selectedVehicle && vehicle ? ` · ${vehicle.name}` : ''}
                              {e.odometer ? ` · ${e.odometer.toLocaleString()} km` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-display font-semibold text-sm">{formatAUD(e.amount)}</span>
                          <button onClick={() => deleteExpense(e.id)} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })
              )}
            </CardContent>
          </Card>

          {/* Vehicle management */}
          <div className="mt-6">
            <h2 className="mb-3 font-display text-lg font-semibold">Your Vehicles</h2>
            <div className="space-y-2">
              {vehicles.map(v => (
                <Card key={v.id}>
                  <CardContent className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-medium text-sm">{v.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {v.rego ? `${v.rego} · ` : ''}{v.workUsePercent}% work use
                      </p>
                    </div>
                    <button onClick={() => deleteVehicle(v.id)} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
